// MIT License - Copyright (c) 2026 Doug McNall

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import * as ReputationManager from '../utils/game/ReputationManager';
import { useCredits } from '../contexts/CreditsContext';
import './DiplomacyModal.css';

/**
 * Diplomacy Modal Component
 * 
 * Dialogue tree system for alien interactions
 */
const DiplomacyModal = ({ isOpen, onClose, alien, onOutcome }) => {
  const [currentNode, setCurrentNode] = useState('greeting');
  const [dialogueHistory, setDialogueHistory] = useState([]);
  const [outcome, setOutcome] = useState(null);
  
  const { addCredits } = useCredits();

  const reputation = ReputationManager.getReputation(alien?.origin || 'Unknown');

  // Dialogue tree structure
  const dialogueTree = {
    greeting: {
      text: getGreeting(),
      options: [
        { 
          id: 'friendly', 
          text: '🤝 Greetings, friend. We come in peace.',
          requires: { minRep: -50 },
          next: 'friendly_response'
        },
        { 
          id: 'trade', 
          text: '💰 We seek trade opportunities.',
          requires: { minRep: -100 },
          next: 'trade_response'
        },
        { 
          id: 'intel', 
          text: '📡 We need information about this sector.',
          requires: { minRep: 0 },
          next: 'intel_response'
        },
        { 
          id: 'aggressive', 
          text: '⚔️ Stand down or face consequences.',
          requires: { minRep: -100 },
          next: 'aggressive_response'
        }
      ]
    },
    
    friendly_response: {
      text: getFriendlyResponse(),
      options: [
        { 
          id: 'gift', 
          text: '🎁 Accept our gift as a token of friendship. (Cost: 500 credits)',
          cost: 500,
          next: 'gift_outcome'
        },
        { 
          id: 'share_tech', 
          text: '🔬 Share technological knowledge.',
          requires: { minRep: 25 },
          next: 'tech_outcome'
        },
        { 
          id: 'continue', 
          text: '👋 Safe travels, friend.',
          next: 'peaceful_end'
        }
      ]
    },
    
    trade_response: {
      text: getTradeResponse(),
      options: [
        { 
          id: 'fair_trade', 
          text: '🤝 Propose fair trade agreement. (Gain: 10% discount)',
          requires: { minRep: 0 },
          next: 'trade_success'
        },
        { 
          id: 'bribe', 
          text: '💵 Offer generous payment. (Cost: 1000 credits)',
          cost: 1000,
          next: 'bribe_outcome'
        },
        { 
          id: 'decline', 
          text: '🚫 Decline trade offer.',
          next: 'neutral_end'
        }
      ]
    },
    
    intel_response: {
      text: getIntelResponse(),
      options: [
        { 
          id: 'pay_intel', 
          text: '💰 Pay for information. (Cost: 750 credits)',
          cost: 750,
          next: 'intel_success'
        },
        { 
          id: 'trade_intel', 
          text: '🔄 Trade information.',
          requires: { minRep: 25 },
          next: 'intel_trade'
        },
        { 
          id: 'leave', 
          text: '👋 Never mind, we\'ll find out ourselves.',
          next: 'neutral_end'
        }
      ]
    },
    
    aggressive_response: {
      text: getAggressiveResponse(),
      options: [
        { 
          id: 'intimidate', 
          text: '💪 Press the advantage.',
          requires: { minRep: -25 },
          next: 'intimidate_outcome'
        },
        { 
          id: 'back_down', 
          text: '🏳️ Back down and apologize.',
          next: 'apology_outcome'
        }
      ]
    },
    
    // Outcome nodes
    gift_outcome: {
      text: `${alien?.name || 'The alien'} accepts your gift gratefully. Your generosity is noted.`,
      isEnd: true,
      outcome: { type: 'reputation', value: 15, message: '+15 Reputation' }
    },
    
    tech_outcome: {
      text: 'Knowledge shared benefits all. A bond of trust is formed.',
      isEnd: true,
      outcome: { type: 'reputation', value: 25, message: '+25 Reputation' }
    },
    
    peaceful_end: {
      text: 'The encounter ends peacefully. Both parties depart as friends.',
      isEnd: true,
      outcome: { type: 'reputation', value: 5, message: '+5 Reputation' }
    },
    
    trade_success: {
      text: 'Trade agreement established! You now receive a 10% discount with this faction.',
      isEnd: true,
      outcome: { type: 'discount', value: 10, message: '10% Trade Discount Unlocked' }
    },
    
    bribe_outcome: {
      text: 'Your generous payment is accepted. Doors open for those with credits.',
      isEnd: true,
      outcome: { type: 'reputation', value: 10, message: '+10 Reputation' }
    },
    
    neutral_end: {
      text: 'The encounter ends without incident. Neither side gains nor loses.',
      isEnd: true,
      outcome: { type: 'neutral', message: 'Neutral Outcome' }
    },
    
    intel_success: {
      text: 'Valuable intelligence acquired! You learn of nearby resources and safe routes.',
      isEnd: true,
      outcome: { type: 'intel', value: 1, message: 'Intel Acquired' }
    },
    
    intel_trade: {
      text: 'Information exchanged. Both parties benefit from shared knowledge.',
      isEnd: true,
      outcome: { type: 'reputation', value: 15, message: '+15 Reputation' }
    },
    
    intimidate_outcome: {
      text: reputation >= 0 
        ? 'Your reputation precedes you. They back down reluctantly.'
        : 'Your threats fall flat. Hostilities escalate!',
      isEnd: true,
      outcome: reputation >= 0 
        ? { type: 'reputation', value: -10, message: '-10 Reputation (Intimidation)' }
        : { type: 'combat', message: 'Combat Initiated!' }
    },
    
    apology_outcome: {
      text: 'Your apology is accepted. Tensions ease, though trust is damaged.',
      isEnd: true,
      outcome: { type: 'reputation', value: -5, message: '-5 Reputation' }
    }
  };

  function getGreeting() {
    if (reputation >= 50) return `${alien?.name || 'The alien'} greets you warmly. Your reputation is well known here.`;
    if (reputation >= 0) return `${alien?.name || 'The alien'} acknowledges your presence cautiously.`;
    if (reputation >= -50) return `${alien?.name || 'The alien'} eyes you with suspicion.`;
    return `${alien?.name || 'The alien'} regards you with open hostility!`;
  }

  function getFriendlyResponse() {
    if (reputation >= 50) return 'Your friendship is valued! How may we assist you?';
    if (reputation >= 0) return 'We appreciate peaceful intentions. Perhaps we can help each other.';
    return 'Your words are kind, but your reputation suggests otherwise...';
  }

  function getTradeResponse() {
    if (reputation >= 25) return 'We would be honored to trade with such esteemed partners!';
    if (reputation >= 0) return 'Trade is acceptable. What do you offer?';
    return 'We trade only with those we trust. Prove your worth first.';
  }

  function getIntelResponse() {
    if (reputation >= 50) return 'For a friend, information flows freely.';
    if (reputation >= 0) return 'Information has value. What will you offer in return?';
    return 'We share nothing with those of ill repute.';
  }

  function getAggressiveResponse() {
    if (reputation >= 0) return 'We do not wish conflict, but we will defend ourselves if necessary.';
    return 'You dare threaten us?! Prepare for battle!';
  }

  const handleChoice = (option) => {
    // eslint-disable-next-line no-unused-vars
    const node = dialogueTree[currentNode];
    
    // Check requirements
    if (option.requires?.minRep && reputation < option.requires.minRep) {
      return; // Option not available
    }
    
    // Check cost
    if (option.cost) {
      // Cost will be deducted when outcome is applied
    }
    
    // Add to history
    setDialogueHistory(prev => [...prev, {
      speaker: 'You',
      text: option.text
    }]);
    
    // Move to next node
    const nextNode = dialogueTree[option.next];
    setCurrentNode(option.next);
    
    // Add response to history
    setDialogueHistory(prev => [...prev, {
      speaker: alien?.name || 'Alien',
      text: nextNode.text
    }]);
    
    // Check if end node
    if (nextNode.isEnd) {
      applyOutcome(nextNode.outcome, option.cost);
    }
  };

  const applyOutcome = (outcomeData, cost) => {
    if (cost) {
      addCredits(-cost);
    }
    
    if (outcomeData.type === 'reputation') {
      ReputationManager.modifyReputation(alien?.origin || 'Unknown', outcomeData.value);
    }
    
    setOutcome(outcomeData);
    
    // Notify parent
    if (onOutcome) {
      onOutcome(outcomeData);
    }
  };

  const handleClose = () => {
    setCurrentNode('greeting');
    setDialogueHistory([]);
    setOutcome(null);
    onClose();
  };

  if (!isOpen || !alien) return null;

  const node = dialogueTree[currentNode];

  return createPortal(
    <div className="diplomacy-overlay" onClick={(e) => e.target.className === 'diplomacy-overlay' && handleClose()}>
      <div className="diplomacy-modal">
        <div className="diplomacy-header">
          <div className="diplomacy-alien-info">
            <span className="diplomacy-alien-icon">{alien.appearance?.ascii || '👽'}</span>
            <div>
              <h2>{alien.name}</h2>
              <div className="diplomacy-reputation">
                Reputation: <span style={{ color: reputation >= 0 ? '#00ff88' : '#ff4444' }}>
                  {reputation}
                </span>
              </div>
            </div>
          </div>
          <button className="diplomacy-close" onClick={handleClose}>✕</button>
        </div>

        <div className="diplomacy-content">
          <div className="dialogue-history">
            {dialogueHistory.length === 0 && (
              <div className="dialogue-message alien-message">
                <span className="dialogue-speaker">{alien.name}:</span>
                <span className="dialogue-text">{node.text}</span>
              </div>
            )}
            {dialogueHistory.map((msg, i) => (
              <div key={i} className={`dialogue-message ${msg.speaker === 'You' ? 'player-message' : 'alien-message'}`}>
                <span className="dialogue-speaker">{msg.speaker}:</span>
                <span className="dialogue-text">{msg.text}</span>
              </div>
            ))}
          </div>

          {!node.isEnd && (
            <div className="dialogue-options">
              {node.options.map(option => {
                const available = !option.requires?.minRep || reputation >= option.requires.minRep;
                return (
                  <button
                    key={option.id}
                    className={`dialogue-option ${!available ? 'disabled' : ''}`}
                    onClick={() => available && handleChoice(option)}
                    disabled={!available}
                  >
                    <span className="option-text">{option.text}</span>
                    {option.cost && <span className="option-cost">💰 {option.cost}</span>}
                    {!available && <span className="option-locked">🔒 Reputation too low</span>}
                  </button>
                );
              })}
            </div>
          )}

          {outcome && (
            <div className="diplomacy-outcome">
              <div className="outcome-message">{outcome.message}</div>
              <button className="outcome-continue" onClick={handleClose}>
                CONTINUE
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DiplomacyModal;
